/* ============================================
   QUOTE DASHBOARD — Quote row (main table <tr>)
   Asset: dashboard-quote-row.js

   Extracted from dashboard-main.js renderTable inner function on 2026-05-23
   as Phase B.3.b SURGICAL of the dashboard refactor. The original Phase B
   plan included a "B.3.b — full renderTable extraction" ship (596 LOC).
   After surfacing the risk profile at the end of the 2026-05-23 session
   (buildQuoteRow alone touches every lifecycle branch + brand badge +
   follow-up countdown + mobile-actions trigger + To-Do icon strip + both
   desktop AND mobile column layouts), the scope narrowed to the SURGICAL
   option: extract buildQuoteRow only, leave chain-grouping + buildDetailRow
   + the orchestration loop in main.

   What this module does:
     - Renders a single quote <tr> for the main dashboard table.
     - Derives lifecycle status (LEAD / SAMPLE / ON HOLD / DEAD / RESUMED /
       '...' / QUOTED / SOLD / LOST / LINKED) from q + chain flags.
     - Handles ditto markers (DIT) on child revision rows where
       Org / Customer / Location / Date / Amount match the parent.
     - Renders the action cog menu (desktop) with branches for
       LEAD/SAMPLE/DEAD/HOLD, PENDING, SOLD, and QUOTED.
     - Embeds the Shopify online-order logo, linked-order 🔗 badge,
       brand-list tooltip badge, and discount-code attribution ⚠ admin
       warning.

   Loaded via <script defer> in sections/rep-quote-dashboard.liquid BEFORE
   main.js. Public API attached to window.RQD:

     buildQuoteRow(q, extraRefHtml, extraClass, parentQ)  -> <tr> element

   Init contract (RQD.initQuoteRow called once by main.js at boot):
     esc, isResidentialOrg, formatDate, money, capitalize,
     buildProgressBar, _qstHasContactActivity, buildRequestLogisticsButtons
       — all function declarations in main, safe to pass by reference per
       the init-deps-var-hoisting-trap memo.

     getIsAdmin, getCurrentSource — closure-getters. Both back `var`s in
     main that mutate after Auth.getSession() resolves (~L301) and on
     source-picker click respectively. Pass as getters so each render
     sees the current value.
   ============================================ */
(function() {
  'use strict';
  window.RQD = window.RQD || {};

  // Injected by RQD.initQuoteRow (from main.js boot)
  var esc, isResidentialOrg, formatDate, money, capitalize;
  var buildProgressBar;
  var _qstHasContactActivity, buildRequestLogisticsButtons;
  var buildLinkExternalButton, buildLinkOpenDealButton;
  var hasExternalOrderLink;
  var getIsAdmin, getCurrentSource;
  /* CUSTOM_REVIEWS_BY_CLAUDE (Ryan 2026-09-09) — the review-ask cog entry (built in main.js) */
  var buildReviewAskMenuItem;

  // Ditto marker for child revision rows where the value matches the
  // parent. Moved verbatim from inside renderTable in main; used only
  // by buildQuoteRow.
  var DIT = '<span class="rqd-ditto">&quot;</span>';

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-25) — reps (by initials) allowed to
     edit the deal Date column, same as admins. Mirrors the
     OUTLOOK_ENABLED_REPS_CLIENT allowlist convention in dashboard-activity-card.js.
     The AUTHORITATIVE gate is server-side in rfq-worker admin.js
     handleQuoteDateOverride (DATE_OVERRIDE_ALLOWED_REPS); this client list only
     controls whether the ✎ button renders. */
  var DATE_EDIT_ALLOWED_REPS = ['JW'];

  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (AIOPS suggestions 1c5409fdb018 + fc5b7ad0298d, 2026-09-02)
     The vendor's own ready-date, on the collapsed row.

     ⛔ THE LABEL IS THE FEATURE. AIOPS filed the field, then filed a SECOND suggestion purely to
     correct what the number means: it is ECORE's "Scheduled Ship" — the day they expect the order
     ready to leave THEIR dock. Not a delivery date, not a Blue Sky pickup appointment. A chip
     reading "ETA Sep 18" would be read by a rep as "the customer gets it on the 18th", and that
     rep would tell a customer so. So it reads "Vendor ready".

     ⚠ AND IT CARRIES ITS AS-OF STAMP. The date is transcribed from a dealer-portal screenshot, so
     it is only as good as when it was taken; a three-week-old vendor date shown as bare fact is
     the failure this chip exists to prevent. Treat it as plus or minus a day — and if ECORE says
     "ready" after ~3pm Denver, the earliest pickup is the next business day, which is what the
     optional note is for. */
  /* CUSTOM_COLDOWNER_BY_CLAUDE (Ryan 2026-09-11) — PHASE 4: THE DEAL NOBODY IS MOVING.
     ─────────────────────────────────────────────────────────────────────────────────────────
     The worker stamps `q.coldOwner` ONLY when a deal's owner has gone quiet past the window in
     Settings → Admin: One Customer, One Deal. It never stamps a false, so the field's presence is
     the whole test — see lib/cold-owner.js for the predicate and its exclusions.

     ⛔ IT SITS BESIDE THE STATUS PILL, IT DOES NOT REPLACE IT. A cold deal is still a Quoted deal
     or a New Lead, and that word is what a rep navigates the board by; overwriting it would trade
     a fact for a warning and leave the rep unable to tell a stalled QUOTE from a stalled LEAD.
     Same slot and the same treatment as the vendor-ETA chip next door, for the same reason.

     ⛔ AND IT SAYS THE NUMBER, NOT JUST "COLD". "Cold" alone is a judgement the reader cannot
     check; "94d" is the evidence, and 94 days versus 61 is the difference between a deal to
     reassign today and one to look at this month. The title names the owner and the date, because
     the action this drives is reassignment and the first question is always "whose is it". */
  /* CUSTOM_REFUNDAFTEREMAIL_BY_CLAUDE (ONYX 06a820791f3b, Ryan 2026-10-17) - a refund Ryan OK'd that waits on an email to the
     customer. The worker puts `q.refundAfterEmail` {state waiting | due, amount, label} on the row (lib/refund-after-email.js
     holdPill); this only draws it. Amber while the email has not gone, red once it has and the refund is still owed (Ryan's
     pick, 2026-10-17). Gone when Shopify shows the refund. */
  function buildRefundAfterEmailChip(q) {
    var r = q && q.refundAfterEmail;
    if (!r || !r.label) return '';
    var due = r.state === 'due';
    var title = due
      ? 'The email this refund was waiting on' + (r.testNumber ? ' (TEST ' + r.testNumber + ')' : '') + ' has gone to the customer. ONYX was woken to issue the refund; you are emailed if it has not gone through 30 minutes later.'
      : 'Refund you OK\'d, waiting for' + (r.testNumber ? ' TEST ' + r.testNumber : ' an email') + ' to reach the customer first. ONYX is woken to issue it the moment it goes.';
    return '<span class="rqd-refund-wait-chip' + (due ? ' rqd-refund-wait-chip--due' : '') + '" title="' + esc(title) + '">' + esc(r.label) + '</span>';
  }
  /* CUSTOM_AGENTOWNER_BY_CLAUDE (AIOPS 16ae83cad814, Ryan 2026-10-17) - the AI agent that owns the deal. The worker puts
     `q.agentOwner` {seat, customer, parts[], supportCase} on the row only while Settings -> Admin: One Owner Per Deal is on; this
     only draws it. Ryan 2026-10-17: it sits at the bottom of the expanded card's LINKED FILES column (not on the board row), ONYX
     reads "Abby", each name shows its mailbox on hover and copies it on click. A shipment another agent owns (Brendan's support
     case) is named after the owner; amber when the deal is split. */
  var AGENT_SEAT_NAMES = { ONYX: 'Abby' };
  var AGENT_SEAT_EMAILS = {
    ONYX: 'orders@blueskyfitsupply.com',
    BRENDON: 'support@blueskyfitsupply.com',
    KAREN: 'compliance@blueskyfitsupply.com',
    ALFRED: 'product@blueskyfitsupply.com'
  };
  function buildAgentOwnerChip(q) {
    var o = q && q.agentOwner;
    if (!o || !o.seat) return '';
    var nm = function (s) { s = String(s || '').toUpperCase(); if (AGENT_SEAT_NAMES[s]) return AGENT_SEAT_NAMES[s]; return s.charAt(0) + s.slice(1).toLowerCase(); };
    var who = function (seat) {
      var key = String(seat || '').toUpperCase();
      var email = AGENT_SEAT_EMAILS[key];
      if (!email) return '<span class="rqd-agent-owner-chip__name">' + esc(nm(key)) + '</span>';
      return '<span class="rqd-agent-owner-chip__name rqd-agent-owner-chip__name--email" role="button" tabindex="0" data-agent-email="' + esc(email)
        + '" title="' + esc(email) + ' (click to copy)">' + esc(nm(key)) + '</span>';
    };
    var bits = [];
    if (o.customer && o.customer !== o.seat) bits.push('customer: ' + who(o.customer));
    (o.parts || []).forEach(function (p) { bits.push(esc(p.vendor + (p.load ? ' load ' + p.load : '')) + ': ' + who(p.seat)); });
    var caseLine = o.supportCase ? '<span class="rqd-agent-owner-chip__case">Support case open (' + esc(o.supportCase.issue) + ')' + (o.supportCase.whole ? ', whole order' : '') + '</span>' : '';
    return '<div class="rqd-agent-owner-chip' + (bits.length ? ' rqd-agent-owner-chip--split' : '') + '">Agent: ' + who(o.seat)
      + (bits.length ? ' &middot; ' + bits.join(' &middot; ') : '') + caseLine + '</div>';
  }
  /* Click a name: copy that agent's mailbox. Delegated once, capture phase so the card's own click handlers never see it. */
  function copyAgentEmail(el) {
    var email = el.getAttribute('data-agent-email');
    if (!email) return;
    var done = function () {
      if (el._rqdCopiedT) clearTimeout(el._rqdCopiedT);
      el.classList.add('rqd-agent-owner-chip__name--copied');
      el.setAttribute('data-copied', 'Copied');
      el._rqdCopiedT = setTimeout(function () { el.classList.remove('rqd-agent-owner-chip__name--copied'); el.removeAttribute('data-copied'); }, 1400);
    };
    var fallback = function () {
      var ta = document.createElement('textarea');
      ta.value = email; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.left = '-9999px';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch (e) {}
      document.body.removeChild(ta); done();
    };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(email).then(done, fallback);
    else fallback();
  }
  if (!window.__rqdAgentEmailCopyWired) {
    window.__rqdAgentEmailCopyWired = true;
    document.addEventListener('click', function (ev) {
      var el = ev.target && ev.target.closest && ev.target.closest('[data-agent-email]');
      if (!el) return;
      ev.preventDefault(); ev.stopPropagation();
      copyAgentEmail(el);
    }, true);
    document.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Enter' && ev.key !== ' ') return;
      var el = ev.target && ev.target.closest && ev.target.closest('[data-agent-email]');
      if (!el) return;
      ev.preventDefault(); ev.stopPropagation();
      copyAgentEmail(el);
    }, true);
  }
  function buildColdChip(q) {
    var c = (q && q.coldOwner && typeof q.coldOwner === 'object') ? q.coldOwner : null;
    if (!c) return '';
    var days = parseInt(c.days, 10);
    if (!isFinite(days) || days < 0) return '';
    var title = 'Nobody has worked this deal in ' + days + ' days'
      + (c.owner ? ' — it belongs to ' + c.owner : '')
      + (c.lastWorkedAt ? '. Last worked ' + String(c.lastWorkedAt).slice(0, 10) : '')
      + '.\nA hold or a follow-up would have kept it off this list; it has neither.'
      + '\nNothing has been reassigned — use Reassign on the deal if it should move.';
    return '<span class="rqd-cold-chip" title="' + esc(title) + '">&#10052; COLD <b>' + days + 'd</b></span>';
  }

  /* CUSTOM_READYBY_BY_CLAUDE (AIOPS b641360cdb02, Ryan 2026-09-16) \u2014 HOW TO SAY ONE SUPPLIER'S
     READY-BY IN A TABLE CELL.

     \u26d4 THE WINDOW'S LAST DAY IS NOT THE ANSWER, and printing it here was a silent lie. Spirit
     answered "sometime in October" on RW-1317-C; the worker stores that as Oct 1\u201331 with `date`
     set to the 31st (four readers key on `date`, and a null there would read to all of them as
     "no date recorded"), and this cell printed "Vendor ready Oct 31" \u2014 their hedge rewritten as
     our commitment, on the board, for anyone who glanced at it.

     \u26a0\ufe0f DERIVED, NOT THE RAW LABEL. A label may be eighty characters and this is a column.
     "early October" becomes "early Oct"; "sometime in October" becomes "in Oct". The supplier's
     own sentence is on the hover, where there is room for it.

     Shared on RQD because the Tracking card says the same thing one screen in, and two formatters
     that agree only today is how the two surfaces drift apart. */
  var _VE_MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function _veParts(d) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(d || ''));
    return m ? { y: parseInt(m[1], 10), m: parseInt(m[2], 10), d: parseInt(m[3], 10) } : null;
  }
  function _veLastDay(y, m1) { return new Date(Date.UTC(y, m1, 0)).getUTCDate(); }

  /** Short enough for the status column. Pure. */
  function readyByShort(ve) {
    if (!ve) return '';
    if (ve.kind === 'unparsed') {
      var w = String(ve.label || '').trim();
      return w.length > 24 ? w.slice(0, 23) + '\u2026' : w;
    }
    if (ve.kind !== 'range') return _veShort(ve.date);
    /* The supplier's own hedge word, which is the whole reason `precision` is stored. */
    if (ve.precision === 'early' || ve.precision === 'mid' || ve.precision === 'late') {
      var a = _veParts(ve.start || ve.date);
      return a ? ve.precision + ' ' + _VE_MON[a.m - 1] : _veShort(ve.date);
    }
    var s1 = _veParts(ve.start);
    var e1 = _veParts(ve.end);
    if (!s1 || !e1) return _veShort(ve.date);
    if (s1.y === e1.y && s1.m === e1.m) {
      /* A whole calendar month reads as "in Oct". "Oct 1\u201331" is the same fact spelled as though
         somebody had measured it. */
      if (s1.d === 1 && e1.d === _veLastDay(e1.y, e1.m)) return 'in ' + _VE_MON[s1.m - 1];
      return _VE_MON[s1.m - 1] + ' ' + s1.d + '\u2013' + e1.d;
    }
    return _VE_MON[s1.m - 1] + ' ' + s1.d + '\u2013' + _VE_MON[e1.m - 1] + ' ' + e1.d;
  }

  /** The window written out, for the card that has room: "10/1\u201310/31/2026". Pure. */
  function readyByRangeText(ve) {
    if (!ve || ve.kind !== 'range') return '';
    var s1 = _veParts(ve.start);
    var e1 = _veParts(ve.end);
    if (!s1 || !e1) return '';
    return s1.m + '/' + s1.d + '\u2013' + e1.m + '/' + e1.d + '/' + e1.y;
  }

  function buildVendorEtaChip(q) {
    var map = (q && q.vendorEta && typeof q.vendorEta === 'object') ? q.vendorEta : null;
    /* CUSTOM_BUILTTOORDER_BY_CLAUDE (ONYX 2600e44ea7a4, Ryan 2026-10-13) - a built-to-order supplier with no date of its own shows
       its DEFAULT range (PO sent + the Vendor Guide's weeks) from `q.vendorEtaDefault`, labelled "(default)" so it never reads as
       the supplier's word. A real date for that supplier always replaces it (the worker drops the default). */
    var defMap = (q && q.vendorEtaDefault && typeof q.vendorEtaDefault === 'object') ? q.vendorEtaDefault : null;
    if (defMap) {
      var merged = {};
      for (var mk in (map || {})) if (Object.prototype.hasOwnProperty.call(map, mk)) merged[mk] = map[mk];
      for (var dk in defMap) if (Object.prototype.hasOwnProperty.call(defMap, dk) && !merged[dk] && defMap[dk]) merged[dk] = defMap[dk];
      map = merged;
    }
    if (!map) return '';
    /* \u26d4 A MAP OF VENDORS, so the chip has to SUMMARISE rather than print a value. A deal can
       carry ECORE ready on the 18th and Legend on the 25th; showing one of them unlabelled is the
       ambiguity the per-vendor rewrite existed to remove, rebuilt one screen further out. */
    var rows = [];
    for (var k in map) {
      if (Object.prototype.hasOwnProperty.call(map, k) && map[k] && map[k].date) {
        rows.push({ vendor: k, entry: map[k], date: map[k].date, asOf: map[k].asOf || '', note: map[k].note || '' });
      }
    }
    if (!rows.length) return '';
    rows.sort(function (a, b) { return a.date < b.date ? -1 : a.date > b.date ? 1 : 0; });

    /* \u26d4 THE LATEST DATE, NOT THE EARLIEST. The question a rep is answering off the board is
       "when can this ship complete", and that is gated by the LAST supplier to be ready. The
       earliest would read as good news the order cannot actually deliver on. */
    var last = rows[rows.length - 1];
    /* Ryan picked this shape on 2026-09-16 from three rendered options: the supplier, then what
       they actually said, and nothing else. The as-of stamp and the "\u00b7 note" marker both moved to
       the hover \u2014 on a freshly stamped deal they were half the cell's width, and neither answers
       the question the column is asked. */
    var label = last.vendor + ' \u00b7 ' + readyByShort(last.entry);
    if (last.entry && last.entry.isDefault) label += ' (default)'; // CUSTOM_BUILTTOORDER_BY_CLAUDE
    /* Still counted, because "Spirit \u00b7 in Oct" on a two-supplier deal is true of one of them and
       silent about the other. */
    if (rows.length > 1) label += ' +' + (rows.length - 1);

    var title = rows.map(function (r) {
      var e2 = r.entry || {};
      /* \u26a0\ufe0f A RANGE SAYS SO OUT LOUD HERE TOO. "ready about Oct 31" on a window is the chip's own
         lie with more room to tell it in. */
      var when = e2.kind === 'range'
        ? 'ready somewhere in ' + (readyByRangeText(e2) || r.date) + (e2.label ? ' \u2014 they said "' + e2.label + '"' : '')
        : e2.kind === 'unparsed'
          ? 'no date we could read \u2014 they said "' + (e2.label || '') + '"'
          : 'ready about ' + r.date + ' (\u00b1 a day)';
      if (e2.isDefault) when = 'no date from them yet; built to order, so the default ' + (e2.label || 'lead time') + ' from the PO puts it ' + (readyByRangeText(e2) || r.date); // CUSTOM_BUILTTOORDER_BY_CLAUDE
      return r.vendor + ': ' + when
        + (r.asOf ? ', taken from the vendor portal ' + r.asOf : '')
        + (r.note ? ' \u2014 ' + r.note : '');
    }).join('\n') + '\nVendor dock dates, not delivery dates.';

    return '<span class="rqd-vendor-eta" title="' + esc(title) + '">' + esc(label) + '</span>';
  }

  /* "2026-09-18" -> "Sep 18". Parsed as parts, never `new Date('YYYY-MM-DD')`, which is treated as
     UTC midnight and renders as the previous day for everyone west of Greenwich \u2014 i.e. everyone
     here. A vendor date shown one day early is exactly the error this chip guards against. */
  function _veShort(d) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(d || ''));
    if (!m) return String(d || '');
    var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return MON[parseInt(m[2], 10) - 1] + ' ' + parseInt(m[3], 10);
  }

  function buildQuoteRow(q, extraRefHtml, extraClass, parentQ) {
    var isAdmin = getIsAdmin();
    var currentSource = getCurrentSource();
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — admin OR an allowlisted rep may edit the date */
    var canEditDate = isAdmin || DATE_EDIT_ALLOWED_REPS.indexOf(
      String(((window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.getSession()) || {}).code || '').toUpperCase()
    ) !== -1;
    var isPending = q.isPending || q.status === 'pending';
    var isLead = q.isLead || q.status === 'new_lead';
    var isDeadLead = !!q.isDeadLead;
    var isSample = !!q.isSample;
    var isOnHold = !!q.isOnHold;
    // Two pre-PDF lead states share most lifecycle prerequisites:
    //   isAwaiting    — fresh CUSTOMER RFQ with no items added yet
    //                   (someone submitted via the storefront and a
    //                   rep hasn't touched it). Yellow NEW LEAD pill
    //                   + pulse + awaiting timer in To Do.
    //   isInProgress  — ANY quote (customer-RFQ-derived OR admin-
    //                   built from scratch) that has items but no
    //                   PDF yet. Blue "..." pill, no pulse, no
    //                   timer. Reads as "quote in flight."
    // Once the PDF is downloaded the row flips to canonical QUOTED.
    // Dead, Sample, and On-Hold rows share the lead-shaped layout
    // but must NOT pulse.
    var _baseClear = !isPending && !isDeadLead && !isSample && !isOnHold && !q.lifecycleStatus && !q.chainQuoted && !(q.chainPdfDownloaded || q.pdfDownloaded);
    var _hasQuoteItems = (parseFloat(q.total) || 0) > 0;
    var _chainHasItemsRow = !!q.chainHasItems || _hasQuoteItems;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-05) — progressed = has items or a
       PDF. On-hold rows force _baseClear=false (so isInProgress is always false
       for them), so the row cog menu needs this independent signal to let a
       built-then-held quote fall through to the QUOTED branch (Edit Quote)
       instead of the lead menu (whose only quote path mints a fresh draft). */
    var _isProgressedRow = _chainHasItemsRow || q.pdfDownloaded || q.chainPdfDownloaded;
    var _isFreshCustomerRfqRow = (q.chainFromCustomerRfq || q.fromCustomerRfq) || q.status === 'submitted' || q.status === 'under_review';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19) — Rep engagement is
       the signal between NEW LEAD and '...' for customer-RFQ rows, not
       "items present." Customer Add-to-Quote submissions arrive WITH
       items (the customer built the cart), so an items-only check
       classified them as in-progress the instant they landed. The
       correct marker is q.chainLastSavedAt — wm.lastSavedAt is null
       on customer-RFQ submission (the rfq-worker writes wm directly,
       bypassing the quote-workstation save path) and only stamps when
       a rep actually saves real content edits. Flow:
         - Customer submits → NEW LEAD (items present, no save)
         - Rep opens & saves no edits → still NEW LEAD (no-op saves
           don't stamp lastSavedAt — see quote-workstation.js)
         - Rep saves real edits → '...' (lastSavedAt stamped)
         - Rep downloads PDF / quote_sent → QUOTED (chainPdfDownloaded
           / chainQuoted flips _baseClear false)
       Reverts the 2026-05-18 "items-present + any source ⇒ in-progress"
       rule which was too aggressive for customer-RFQ flow. */
    var isAwaiting = _baseClear && _isFreshCustomerRfqRow && !q.chainLastSavedAt;
    var isInProgress = _baseClear && !isAwaiting && (_chainHasItemsRow || q.chainLastSavedAt);
    var lsLabel = isPending ? 'pending' : isDeadLead ? 'dead' : isSample ? 'sample' : isOnHold ? 'hold' : isAwaiting ? 'awaiting' : isInProgress ? 'in-progress' : (q.lifecycleStatus || 'quoted');
    var isSold = lsLabel === 'sold';
    // ON HOLD pill is just "ON HOLD" — the wake date now lives on the To
    // Do column countdown (⏳ + remaining time), so duplicating it here
    // would be redundant. The pause symbol was dropped per Ryan: too
    // visually heavy when the row's indigo tint already signals state.
    var statusClass = isPending ? 'pending' : isDeadLead ? 'dead' : isSample ? 'sample' : isOnHold ? 'hold' : isAwaiting ? 'awaiting' : isInProgress ? 'quoted' : (lsLabel === 'expired' ? 'expired' : isSold ? 'sold' : lsLabel === 'lost' ? 'lost' : (q.closedStatus === 'closed' ? 'closed' : 'quoted'));
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Status pill reflects lifecycle
       OUTCOME (Sold / Lost / Quoted), not closedness. A sold-and-fully-
       closed order keeps reading "Sold" here; the green "✓ Closed" chip
       in the To Do column carries the closedness signal (mirrors how
       online fulfilled orders read "Sold" + "✓ Fulfilled"). closedStatus
       is no longer consulted for the status pill text. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Resurrected-from-hold rows wear a
       "🔔 RESUMED" pill instead of "New Lead". Color scheme stays the
       awaiting orange/yellow (statusClass = 'awaiting' below) so the
       rep still treats it as urgent, but the text signals "this is
       a returning lead, not a fresh inbound" so the rep opens the
       conversation correctly. Gate is isAwaiting (same as New Lead)
       AND q.wokenFromHold — once the rep adds items, isAwaiting goes
       false and the pill transitions normally to QUOTED. */
    /* Ryan 2026-05-16: 🔔 dropped from RESUMED pill — the row's
       To-Do column already shows a 🔔 for any RESUMED deal, so
       duplicating the icon on the status pill is redundant. */
    /* Phase 1.40 — CFA-handoff leads in the NEW LEAD lifecycle
       swap "🔥 NEW LEAD" for "🎯 CFA LEAD" so the rep can spot
       handoff origin at a glance without losing the underlying
       awaiting/lead color treatment (still amber). Detected via
       q.cfaCode + q.handoffAt presence; only applied while the
       lead is still in the awaiting pre-quote state. */
    var _isCfaHandoffLead = !!(q.cfaCode && q.handoffAt);
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-11-02) — NEW LEAD pill wears the lead's
       intake-source icon so the rep sees where it came from at a glance. Keyed on
       q.leadCaptureSource (the lead record's `source`, via the worker's lead-fields.js) —
       NOT q.leadSource, which is the commission tag. Icons mirror the worker's
       lib/lead-source-display.js. Room Designer arrives through the Add to Quote path
       (source 'rfq'), so it is told apart by formId. Anything unknown keeps 🔥. */
    var _NEW_LEAD_ICONS = {
      'weight-designer': '🏋️', 'rfq': '🛒', 'manual': '📞', 'email-lead': '📧',
      'chatbot': '💬', 'contact-form': '📝', 'vertical-form': '📝', 'bulk-quote': '📝',
      'blog-cta': '📰', 'sample-request': '🧪'
    };
    var _captureSrc = String(q.leadCaptureSource || '');
    var _srcIcon = (q.leadFormId === 'room-designer') ? '📐' : (_NEW_LEAD_ICONS[_captureSrc] || '');
    var _newLeadIcon = _srcIcon || '🔥';
    /* (Ryan 2026-11-02) OFF HOLD wears the same source icon; an unknown source stays plain
       'OFF HOLD' (no 🔥 — it never had one). */
    var statusText = isPending ? 'Pending' : isDeadLead ? '💀 LEAD' : isSample ? '🧪 SAMPLE' : isOnHold ? '⏳ ON HOLD' : (isAwaiting && q.wokenFromHold) ? (_srcIcon ? _srcIcon + ' OFF HOLD' : 'OFF HOLD') : (isAwaiting && _isCfaHandoffLead) ? '🎯 CFA LEAD' : isAwaiting ? (_newLeadIcon + ' NEW LEAD') : isInProgress ? '...' : capitalize(lsLabel);
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — LINKED status override. A cross-source
       child (q.linkedParentRef set, surviving in the main list because
       the rollup didn't splice it) reads as part of a linked deal, not
       as a standalone SOLD/QUOTED row. Override the visual pill so
       admin sees the relationship at a glance. Underlying lifecycleStatus
       stays as-is — this is purely a display override. Stats already
       exclude these rows on the worker side, so the metric tiles don't
       double-count regardless of the visible status label. */
    var isLinkedChild = !!q.linkedParentRef && !isPending && !isDeadLead && !isSample && !isOnHold && !isAwaiting;
    if (isLinkedChild) {
      statusClass = 'linked';
      statusText = 'LINKED';
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08 PM) — external-order link wears the SAME
       pill. Ryan, comparing RW-1290-A/SHOP-1922 against RW-1126-A/SHOP-1689: "just need there to
       be uniformity." Both are, to a rep reading the board, the same fact — this deal's sale sits
       on a Shopify order — so they get the same chrome.

       ⚠️ They are NOT the same relationship underneath, which is why the TOOLTIP differs (set at
       the status cell below). A balance child's totals roll UP to its parent; an externally
       linked deal OWNS its money — the $9,699, the GP and the commission are on RW-1290-A, and
       the order is the satellite. Same pill, honest hover text. This is display-only, exactly
       like the override above: lifecycleStatus stays 'sold' and every counting surface is
       untouched.

       Both directions qualify: the offline deal (soldBasis.source === 'external') and the online
       order carrying the back-pointer tag (externalLinkedToRef). Same guards as isLinkedChild so
       a lead/sample/held row can't be relabelled out from under its own state. */
    var isExternallyLinked = !isLinkedChild && !isPending && !isDeadLead && !isSample && !isOnHold && !isAwaiting
      && hasExternalOrderLink(q);
    if (isExternallyLinked) {
      statusClass = 'linked';
      statusText = 'LINKED';
    }
    /* END CUSTOM_DEALDASHBOARD_BY_CLAUDE */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-21) — REPLACEMENT pill. A $0 re-ship of
       goods the customer already paid for. Placed ahead of CANCELLED so a cancelled
       replacement still reads CANCELLED, and after the LINKED overrides because a
       replacement is deliberately NOT a linked child (its money never rolls up to the
       parent — see lib/replacement-order.js in the worker). */
    if (q.isReplacement) {
      statusClass = 'replacement';
      /* ⛔ "REPLACE", not "REPLACEMENT" (Ryan 2026-08-22). `.rqd-status` is a FIXED 94px
         pill with white-space: nowrap — roughly 78px of text room once padding is off —
         so a label longer than about nine characters overflows it. "↩ REPLACEMENT" was
         thirteen and spilled out of the column. "CANCELLED" is the longest label that has
         ever fitted, and this now sits in the same class. Shortening the word is the fix,
         not widening the pill: the width is deliberate (bumped 88→94px in 2026-05 for
         "🔥 NEW LEAD") and every sibling label pads up to it so the status column reads as
         one clean vertical line. */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-22) — THE PILL TRACKS THE GOODS.
         REPLACE → ORDERED → SHIPPED, derived server-side from the supplier PO and the vendor
         ordering map (lib/replacement-order.js replacementFulfilmentStage), so it can never
         disagree with what actually happened and nobody has to remember to set it.
         ⛔ The CLASS never changes — always the amber `replacement` palette, so no stage of this
         can read as a sale on a board full of real ones. Only the word changes.
         ⛔ All three labels are 9 characters, which is what `.rqd-status` (a fixed 94px nowrap
         pill) has room for. Do not lengthen one without re-checking that. */
      var _stageLabels = (window.RQD && window.RQD.REPLACEMENT_STAGE_LABELS) || {};
      statusText = _stageLabels[q.replacementStage] || '↩ REPLACE';
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-08) — CANCELLED status pill (red, like
       LOST). A cancelled deal overrides the pill regardless of its prior sold state. */
    if (q.cancelled) {
      statusClass = 'cancelled';
      statusText = 'CANCELLED';
    }
    /* CUSTOM_SIGNEDORDERPENDING_BY_CLAUDE (ONYX 51b7fca781f6, Ryan 2026-10-16) - sold and paid, signed order still owed:
       the pill reads PENDING in the SOLD green (the class stays `sold`, so every sold filter and tint still applies). */
    var _soPendingPill = statusClass === 'sold' && !!q.signedOrderPending;
    if (_soPendingPill) statusText = 'PENDING';
    /* CUSTOM_SIGNEDCOPYGATE_BY_CLAUDE (Ryan 2026-10-22, RW-1532-A) - paid, but no signed copy on file: NOT sold. The pill reads
       AWAIT SIG (9 characters, the width `.rqd-status` has) and the hover says the rest (who is getting it). */
    /* CUSTOM_AWAITSIGPILL_BY_CLAUDE (Ryan 2026-10-22, RW-1532-A) - it reads "✍️ SIGNATURE" in the SOLD green. The green is
       paint only (`rqd-status--awaitsig`): the class token stays the deal's own (quoted), so no sold filter, count or total
       takes it in. The label is wider than the 94px pill's text room at 11px, so that class also sets 10px (measured). */
    var _awaitCopyPill = !!q.awaitingSignedCopy && statusClass !== 'sold' && !q.cancelled;
    if (_awaitCopyPill) statusText = '✍️ SIGNATURE';
    /* CUSTOM_SIGNEDUNPAID_BY_CLAUDE (card 0a13de9f8439, Ryan 2026-10-24) - the mirror: signed, nothing paid yet. */
    var _awaitPayPill = !!q.signedAwaitingPayment && !_awaitCopyPill && statusClass !== 'sold' && !q.cancelled;
    if (_awaitPayPill) statusText = '🫰 PAYMENT';

    // Ditto helpers: show " mark if child value matches parent
    var isChild = !!parentQ;
    var orgVal = isResidentialOrg(q.customerCompany) ? '–' : esc(q.customerCompany || '');
    var custVal = esc(q.customer);
    var locVal = esc(q.location);
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-09) — repeat-buyer pill. Compact 🔁 next
       to the customer name when the server flags a prior SOLD deal on this email/phone
       (or the rep's manual override). Icon-only + tooltip keeps the tight table cell
       readable. Comp-neutral marker. */
    var _rbCount = q.repeatBuyerCount || 1;
    var repeatPill = q.isRepeatBuyer
      ? ' <span class="rqd-repeat-pill" title="Repeat buyer — ' + _rbCount + ' prior closed deal' + (_rbCount === 1 ? '' : 's') + '">🔁</span>'
      : '';
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-07-24, DNI P0) — ad-verified pill. 🎯 next to the
       customer name when the server resolved this deal's ad channel from a DNI tracking-number
       call (q.adChannel, from callattr). Icon-only + tooltip like the repeat pill; read-only. */
    var _adCh = q.adChannel && q.adChannel.channel;
    var _adLabels = { google: 'Google', meta: 'Meta', bing: 'Microsoft', openai: 'OpenAI', 'paid-other': 'Paid ad' };
    var adPill = _adCh
      ? ' <span class="rqd-repeat-pill" title="Ad-verified: ' + esc(_adLabels[_adCh] || _adCh) + (q.adChannel.dialedNumber ? ' — called ' + esc(q.adChannel.dialedNumber) : '') + '">🎯</span>'
      : '';
    var isPend = q.isPending || q.status === 'pending';
    // Show "–" for any blank location, not just Pending rows. Pre-fix
    // QUOTED / NEW LEAD / SOLD rows with no location rendered as
    // empty cells while Pending rows showed the dash — inconsistent.
    if (!q.location) locVal = '–';
    var dateVal = formatDate(q.createdAt);
    var amtVal = isPend ? '–' : money(q.total);

    if (isChild) {
      var pOrg = isResidentialOrg(parentQ.customerCompany) ? '–' : esc(parentQ.customerCompany || '');
      var pCust = esc(parentQ.customer);
      var pLoc = esc(parentQ.location);
      // Ditto org/customer/location/date/amount by display string (always meaningful)
      if (orgVal && orgVal === pOrg) orgVal = DIT;
      if (custVal === pCust) custVal = DIT;
      if (locVal === pLoc) locVal = DIT;
      if (dateVal === formatDate(parentQ.createdAt)) dateVal = DIT;
      var rawAmt = parseFloat(q.total) || 0;
      var rawPAmt = parseFloat(parentQ.total) || 0;
      if (rawAmt > 0 && rawAmt === rawPAmt) amtVal = DIT;
    }

    var tr = document.createElement('tr');
    tr.dataset.ref = q.reference;
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-27) — PHASE 2, THE DOM CONTRACT.
       A row's state has only ever existed as a CSS class (`rqd-row--sold`, `--awaiting`, …), so
       anything reading this board had to know the class vocabulary AND know the special cases —
       a cancelled row carries `rqd-row--cancelled rqd-row--lost` and reads as lost. That is fine
       for styling and useless as an interface.

       `data-status` states the canonical token once. It is the same value `statusClass` drives the
       classes from, so the two cannot drift, and it follows the pattern already established on the
       Purchasing card (dashboard-po-lens.js `stepAttrs`), written for the same reason: the
       fulfillment bot will read the DOM, not the English.

       ⛔ ADDITIVE ONLY. Every existing class stays exactly as it was — no CSS selector, no
       handler and no visual changes here. `data-cancelled` is separate BECAUSE `statusClass` is
       'lost' for a cancelled deal: collapsing them is what makes the class vocabulary lie. */
    tr.dataset.status = statusClass || '';
    if (q.cancelled) tr.dataset.cancelled = 'true';
    if (isLinkedChild) tr.dataset.linkedParent = q.linkedParentRef || '';
    tr.dataset.source = q.source || 'offline';
    tr.style.cursor = 'pointer';
    var cls = extraClass || '';
    if (statusClass === 'sold') cls = (cls ? cls + ' ' : '') + 'rqd-row--sold';
    if (statusClass === 'lost') cls = (cls ? cls + ' ' : '') + 'rqd-row--lost';
    if (statusClass === 'quoted') cls = (cls ? cls + ' ' : '') + 'rqd-row--quoted';
    if (isAwaiting) cls = (cls ? cls + ' ' : '') + 'rqd-row--awaiting';
    if (isDeadLead) cls = (cls ? cls + ' ' : '') + 'rqd-row--dead';
    if (isSample) cls = (cls ? cls + ' ' : '') + 'rqd-row--sample';
    if (isOnHold) cls = (cls ? cls + ' ' : '') + 'rqd-row--hold';
    if (isPending) cls = (cls ? cls + ' ' : '') + 'rqd-row--pending';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — cancelled-row visual treatment.
       Ryan 2026-07-08: cancelled deals mirror LOST. Add rqd-row--lost so the
       row inherits the red border/hover-fill/expanded/follow-up-countdown
       styling (statusClass='cancelled' above suppressed rqd-row--lost, so add
       it explicitly here). rqd-row--cancelled stays as the semantic/JS hook
       (cog gating + pill) but no longer mutes the row. */
    if (q.cancelled) cls = (cls ? cls + ' ' : '') + 'rqd-row--cancelled rqd-row--lost';
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    if (cls) tr.className = cls;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Brand list on REF hover. Every quote
       tracks the vendors on it server-side (q.financials.vendors).
       data-brands-tooltip on the whole <td> drives a CSS-backed
       tooltip with a ~150ms appearance delay (vs the native title
       attribute's untunable ~1s). Cell-wide hover area instead of
       just the bolded ref text. The "•N" badge only renders when
       N>=2 so reps can scan for multi-vendor quotes at a glance. */
    var _refVendors = (q.financials && Array.isArray(q.financials.vendors))
      ? q.financials.vendors.filter(Boolean) : [];
    // data-brands-tooltip carries the flat-text version (legacy plain-
    // text tooltip). data-brand-list carries the pipe-delimited vendor
    // names so the new popover-style tooltip can render them as a
    // selectable bullet list with a label. Pipe (not comma) so vendor
    // names containing commas survive verbatim.
    var _refVendorTooltip = '';
    if (_refVendors.length === 1) _refVendorTooltip = 'Brand: ' + _refVendors[0];
    else if (_refVendors.length >= 2) _refVendorTooltip = 'Brands (' + _refVendors.length + '): ' + _refVendors.join(', ');
    var _refVendorTdAttr = _refVendorTooltip
      ? ' data-brands-tooltip="' + esc(_refVendorTooltip) + '" data-brand-list="' + esc(_refVendors.join('|')) + '"'
      : '';
    var _refVendorBadge = _refVendors.length >= 2
      ? '<span class="rqd-col-ref__brands" aria-hidden="true">•' + _refVendors.length + '</span>'
      : '';
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Online tab only: render the Shopify logo
       inline left of the ref number so we can hide the Status column entirely
       and reclaim 92px for Org/Customer/Location. On the All tab the column
       stays visible and the logo lives there as before, so we don't render
       it twice. */
    var refShopifyLogo = (q.source === 'online' && currentSource === 'online')
      ? '<span class="rqd-col-ref__shopify" title="Online Shopify order" aria-label="Shopify online order">'
        + '<svg width="13" height="15" viewBox="0 0 23 26" xmlns="http://www.w3.org/2000/svg">'
        + '<path fill="#95BF47" d="M19.486 4.667c-.012-.092-.087-.142-.15-.142-.062 0-1.345-.025-1.345-.025s-1.07-1.038-1.183-1.149c-.112-.111-.337-.077-.424-.052-.013.004-.23.071-.591.183-.358-1.027-.987-1.97-2.094-1.97-.03 0-.062.001-.094.003C13.291 1.097 12.91.95 12.578.95c-2.55 0-3.768 3.179-4.151 4.795-.991.306-1.696.524-1.785.553-.553.173-.572.19-.644.711-.054.394-1.5 11.541-1.5 11.541l11.262 2.108L21.69 19.34s-2.193-14.583-2.204-14.673z"/>'
        + '<path fill="#5E8E3E" d="M19.337 4.525c-.062 0-1.345-.025-1.345-.025s-1.07-1.038-1.183-1.149a.262.262 0 0 0-.149-.07v18.95l5.027-1.083s-2.193-14.583-2.204-14.673c-.012-.092-.087-.142-.15-.142z"/>'
        + '<path fill="#FFFFFF" d="M13.482 8.247l-.652 1.927c-.385-.205-.836-.327-1.342-.327-1.082 0-1.143.677-1.143.847 0 .929 2.422 1.286 2.422 3.461 0 1.711-1.085 2.812-2.546 2.812-1.751 0-2.647-1.092-2.647-1.092l.469-1.553s.921.79 1.7.79c.508 0 .714-.4.714-.694 0-1.211-1.987-1.265-1.987-3.255 0-1.674 1.205-3.295 3.633-3.295.937.001 1.379.32 1.379.32z"/>'
        + '</svg></span>'
      : '';
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — At-a-glance linked-order indicator.
       🔗 emoji shows on the right of the ref when this row is linked
       to another quote/order via the balance-children mechanism:
         - Parent: has q.balanceChildren[] populated → tooltip lists
           every child ref.
         - Child: has q.linkedParentRef set (cross-source case where
           the child wasn't spliced from the main list — typically an
           offline workstation quote with an Existing Credit pointing
           at an online Shopify order parent) → tooltip names the
           parent.
       Same-source children are already spliced out of the main list
       so they won't render anywhere except inside the parent's
       Balance Chain panel — no badge needed on them. */
    var refLinkBadge = '';
    if (q.balanceChildren && q.balanceChildren.length > 0) {
      var _linkedChildRefs = q.balanceChildren.map(function(c) { return c.ref || ''; }).filter(Boolean).join(', ');
      refLinkBadge = ' <span class="rqd-col-ref__linked" title="Linked to: ' + esc(_linkedChildRefs) + '" aria-label="Linked order">🔗</span>';
    } else if (q.linkedParentRef) {
      refLinkBadge = ' <span class="rqd-col-ref__linked" title="Linked to: ' + esc(q.linkedParentRef) + '" aria-label="Linked order">🔗</span>';
    } else if (q.externalLinkedToRef) {
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08) — external-order link, order side.
         Same 🔗 affordance, different relationship: this order isn't a balance child, it IS the
         payment for an offline deal that was worked separately. Without it the order sits on the
         Online tab looking like an unattached walk-up sale, and the two rows read as duplicates
         with no visible relationship. Deliberately NOT wired through linkedParentRef — that
         field feeds the balance rollup, which would sum this order into a deal whose financials
         were already rewritten from it. */
      refLinkBadge = ' <span class="rqd-col-ref__linked" title="Paid against deal ' + esc(q.externalLinkedToRef) + ' — that deal owns the notes, lead source and commission" aria-label="Linked to a deal">🔗</span>';
    } else if (q.openDealMatchRef) {
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08) — unlinked match. This customer still
         has an open deal on the board; the sale probably belongs to it. Advisory only — the
         action lives in the cog, a human confirms. */
      refLinkBadge = ' <span class="rqd-col-ref__match" title="Matches open deal ' + esc(q.openDealMatchRef) + ' (same ' + (q.openDealMatchOn === 'company' ? 'company' : 'email') + ') — link it from the cog menu" aria-label="Matches an open deal">&#9889;</span>';
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19) — Phase 3.5: discount-
       code attribution admin badge. When an online order was rep-
       attributed via a registered discount code but admin hasn't yet
       set leadSource (so commission can't auto-compute), surface a ⚙
       cogwheel next to the ref. Admin sees + hovers → tooltip shows
       rep + code; admin opens the row to set leadSource. Reps don't
       see it (only admin path renders; non-admins are read-only). */
    var attrBadge = '';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19) — Hide the ⚠
       leadSource-needed badge on the Offline tab for admin viewing
       an attributed online row. Admins manage these from the Online
       Deals tab; the Offline-tab view is a read-only mirror to
       match what the rep sees. The badge stays visible on Online +
       All tabs where admin does the actual edit. */
    if (isAdmin && currentSource !== 'offline' && q.needsLeadSourceSetup && q.attribution) {
      attrBadge = ' <span class="rqd-col-ref__attr-warn"'
        + ' title="Code-attributed sale: rep=' + esc(q.attribution.repCode || '')
        + ', code=' + esc(q.attribution.code || '')
        + '. Set leadSource to compute commission."'
        + ' aria-label="Needs leadSource">⚠️</span>';
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    tr.innerHTML =
      '<td class="rqd-expand-cell"><span class="rqd-expand-icon">+</span></td>' +
      '<td class="rqd-col-ref"' + _refVendorTdAttr + '>' + refShopifyLogo + (isChild ? '<span class="rqd-child-arrow">&#8627;</span> ' : '') + '<strong>' + esc(q.reference) + '</strong>' + (q.isDealGroup ? ' <span class="rqd-deal-group-icon" title="Linked deal — grouped as one">&#128279;</span>' : '') + refLinkBadge + attrBadge + _refVendorBadge + (extraRefHtml || '') + '</td>' +
      '<td class="rqd-col-org">' + orgVal + '</td>' +
      '<td class="rqd-col-cust">' + (isChild && custVal === DIT ? DIT : esc(q.customer) + repeatPill + adPill + (q.location ? '<span class="rqd-col-cust__loc">' + esc(q.location) + '</span>' : '')) + '</td>' +
      '<td class="rqd-col-loc">' + locVal + '</td>' +
      '<td class="rqd-col-phone"></td>' +
      '<td class="rqd-col-amount">' + amtVal + '</td>' +
      '<td class="rqd-col-date">' + (isChild && dateVal === DIT ? DIT : formatDate(q.createdAt) + (canEditDate && !isChild && q.source !== 'online' && !q.isPending ? ' <button type="button" class="rqd-date-edit-btn" data-ref="' + esc(q.reference) + '" data-current="' + esc(String(q.createdAt || '').slice(0, 10)) + '" title="Edit date" aria-label="Edit date">✎</button>' : '')) + '</td>' +
      // Phase 3.5: reps viewing attributed online rows get the regular
      // SOLD pill (familiar offline chrome); admin keeps the Shopify-
      // logo readonly pill (signals "managed in Shopify Admin") on
      // the Online/All tabs. On the Offline tab admin sees the same
      // rep view (since admin will manage from Online tab anyway).
      '<td class="rqd-col-status">' + (q.source === 'online' && isAdmin && currentSource !== 'offline'
        ? '<span class="rqd-status rqd-status--shopify rqd-status--readonly" data-ref="' + esc(q.reference) + '" title="Online order — change status via cancel/refund in Shopify Admin">'
          + '<svg class="rqd-status__shopify-icon" viewBox="0 0 23 26" xmlns="http://www.w3.org/2000/svg" aria-label="Shopify online order">'
          + '<path fill="#95BF47" d="M19.486 4.667c-.012-.092-.087-.142-.15-.142-.062 0-1.345-.025-1.345-.025s-1.07-1.038-1.183-1.149c-.112-.111-.337-.077-.424-.052-.013.004-.23.071-.591.183-.358-1.027-.987-1.97-2.094-1.97-.03 0-.062.001-.094.003C13.291 1.097 12.91.95 12.578.95c-2.55 0-3.768 3.179-4.151 4.795-.991.306-1.696.524-1.785.553-.553.173-.572.19-.644.711-.054.394-1.5 11.541-1.5 11.541l11.262 2.108L21.69 19.34s-2.193-14.583-2.204-14.673z"/>'
          + '<path fill="#5E8E3E" d="M19.337 4.525c-.062 0-1.345-.025-1.345-.025s-1.07-1.038-1.183-1.149a.262.262 0 0 0-.149-.07v18.95l5.027-1.083s-2.193-14.583-2.204-14.673c-.012-.092-.087-.142-.15-.142z"/>'
          + '<path fill="#FFFFFF" d="M13.482 8.247l-.652 1.927c-.385-.205-.836-.327-1.342-.327-1.082 0-1.143.677-1.143.847 0 .929 2.422 1.286 2.422 3.461 0 1.711-1.085 2.812-2.546 2.812-1.751 0-2.647-1.092-2.647-1.092l.469-1.553s.921.79 1.7.79c.508 0 .714-.4.714-.694 0-1.211-1.987-1.265-1.987-3.255 0-1.674 1.205-3.295 3.633-3.295.937.001 1.379.32 1.379.32z"/>'
          + '</svg>'
          + '</span>'
        /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-27) — `data-status` beside the class, for
           the same reason it was added to the <tr>: the token is the interface, the class is the
           paint. The visible label IS rendered, so text was already recoverable — but the label
           and the token are not the same thing ("NEW LEAD" vs `awaiting`), and two pills reading
           LINKED mean opposite things about where the money lives. */
        : '<span class="rqd-status rqd-status--' + statusClass + (_awaitCopyPill || _awaitPayPill ? ' rqd-status--awaitsig' : '') + '" data-status="' + esc(statusClass) + '" data-ref="' + esc(q.reference) + '"'
          + (isLinkedChild ? ' title="Linked to ' + esc(q.linkedParentRef) + ' — totals roll up to the parent"' : '')
          /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08 PM) — same LINKED pill, opposite
             relationship, so the hover must not repeat the balance-child wording. Here the money
             stays on the row you're looking at (deal side) or on the deal named (order side). */
          + (isExternallyLinked
            ? (q.externalLinkedToRef
              ? ' title="Payment for deal ' + esc(q.externalLinkedToRef) + ' — that deal holds the notes, GP and commission"'
              : ' title="Sold via Shopify order ' + esc((q.soldBasis && q.soldBasis.externalOrderName) || '') + ' — the totals and commission stay on this deal"')
            : '')
          + (_soPendingPill ? ' title="Sold and paid. The signed order form is still owed; Abby is asking the customer to sign the proposal."' : '')
          + (_awaitCopyPill ? ' title="' + esc((q.awaitingSignedCopy && q.awaitingSignedCopy.label) || 'Paid - awaiting signed copy') + '. Not sold yet: nothing is ordered until the signed copy is on file."' : '') // CUSTOM_SIGNEDCOPYGATE_BY_CLAUDE
          + (_awaitPayPill ? ' title="Signed' + (q.signedAwaitingPayment.at ? ' ' + esc(String(q.signedAwaitingPayment.at).slice(0, 10)) : '') + '. Waiting on payment: not sold until it arrives."' : '') // CUSTOM_SIGNEDUNPAID_BY_CLAUDE
          + '>'
          // Phase 3.5: rep view of attributed online (or admin on
          // the Offline tab, where the row mirrors rep view) gets
          // a globe icon to the left of SOLD so the source is
          // legible at a glance even with the offline-style chrome.
          // Inline SVG w/ currentColor so the glyph inherits the
          // SOLD pill's dark-green text color (#052e16).
          + (q.source === 'online' && (!isAdmin || currentSource === 'offline')
            ? '<span class="rqd-status__online-icon" title="Sold online via Shopify" aria-label="Sold online">'
              + '<svg viewBox="0 0 20 20" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true">'
              + '<circle cx="10" cy="10" r="8"/>'
              + '<ellipse cx="10" cy="10" rx="4" ry="8"/>'
              + '<path d="M2 10h16"/>'
              + '</svg>'
              + '</span>'
            : '')
          + statusText
          + '</span>'
      ) + ((window.RQD && window.RQD.buildPaymentHoldChip) ? window.RQD.buildPaymentHoldChip(q) : '') /* CUSTOM_PAYMENTCHECK_BY_CLAUDE */
        + buildRefundAfterEmailChip(q) /* CUSTOM_REFUNDAFTEREMAIL_BY_CLAUDE */
        + buildColdChip(q) + '</td>' + /* CUSTOM_REPREADYDATE_BY_CLAUDE (Ryan 2026-10-22) - the supplier ready line left this column: reps read it on the Summary card, admins on the Tracking card. buildVendorEtaChip stays for its formatter tests. */
      '<td class="rqd-col-progress"><div class="rqd-progress-cell">' + buildProgressBar(q) +
        '<button class="rqd-actions-trigger" type="button" data-menu-ref="' + esc(q.reference) + '" data-action="open-row-actions" aria-haspopup="menu" aria-expanded="false" aria-label="Actions for ' + esc(q.reference) + '" title="Actions for this deal">&#9881;</button>' +
        '<div class="rqd-actions-menu" data-menu-for="' + esc(q.reference) + '">' +
          (((isLead && !isOnHold && !isInProgress && !(q.pdfDownloaded || q.chainPdfDownloaded)) || isDeadLead || isSample || (isOnHold && !_isProgressedRow))
            // LEAD / SAMPLE / DEAD / ON HOLD branch.
            //   NEW LEAD (fresh — no items, no PDF): Create Quote (→
            //     /create-quote-from-lead). Mark Dead appears only after
            //     72h. Put On Hold available.
            //   SAMPLE: Promote to Lead. Mark Dead always available.
            //     Put On Hold available.
            //   DEAD: Resurrect Lead.
            //   ON HOLD: Wake Now (→ /lead-wake). Edit Hold Until.
            // Note: NEW LEAD that has progressed (items added → '...'
            // pill, or PDF downloaded → 'Quoted' pill) FALLS THROUGH
            // to the standard QUOTED branch below. The rep's mental
            // model at that point is "this is a quote" — they want
            // Edit Quote / Download PDF / Reassign / Request Logistics
            // / Delete, not Create Quote / Hold / Mark Dead.
            /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-22) — `!isOnHold` on the
               first (isLead) term so the dedicated `(isOnHold && !_isProgressedRow)`
               term is the SOLE authority for on-hold rows. A lead-originated deal
               built into a quote then put on hold carries isLead + isOnHold +
               total>0 (worker Object.assigns leadFields onto the existing quote),
               but on-hold rows force _baseClear=false → isInProgress=false, so the
               isLead term used to catch it → "Create Quote" (mints a fresh draft,
               orphans the work). Now it falls through to QUOTED → Edit Quote.
               Mirrors the mobile cog's `_isFreshLeadDH` guard (dashboard-detail-
               shell.js). Fixed JW-1195-A (held quote, freight requested, no Edit). */
            ? (function() {
                /* Ryan 2026-05-21: Activity gate replaces 72h age gate.
                   Rep must log a Note / Phone Call / Email (or any chip)
                   on the Activity card before the row offers Mark Dead.
                   Samples + ON HOLD continue to bypass (sample noise is
                   binary; ON HOLD always went through a note-bearing
                   Put-on-Hold flow). */
                var canMarkDead = !isDeadLead && (isSample || isOnHold || _qstHasContactActivity(q));
                /* Ryan 2026-05-16: ON HOLD can skip Remove-Hold-
                   then-Create-Quote two-step — Create Quote is the
                   primary action here too. "Remove Hold" follows
                   as a secondary action (replaces "Wake Now"). */
                var primaryBtn, secondaryActionBtn;
                if (isDeadLead) {
                  primaryBtn = '<button type="button" class="rqd-resurrect-lead-btn" data-ref="' + esc(q.reference) + '">Resurrect Lead</button>';
                  secondaryActionBtn = '';
                } else if (isOnHold) {
                  primaryBtn = '<button type="button" class="rqd-build-quote-from-lead-btn" data-ref="' + esc(q.reference) + '">Create Quote</button>';
                  secondaryActionBtn = '<button type="button" class="rqd-wake-lead-btn" data-ref="' + esc(q.reference) + '">⏵ Remove Hold</button>';
                } else {
                  // NEW LEAD + SAMPLE: build-from-lead (mints fresh draft via
                  // /create-quote-from-lead). Label "Create Quote" not "Edit Quote".
                  primaryBtn = '<button type="button" class="rqd-build-quote-from-lead-btn" data-ref="' + esc(q.reference) + '">Create Quote</button>';
                  secondaryActionBtn = '';
                }
                var holdBtn = isOnHold
                  ? '<button type="button" class="rqd-edit-hold-btn" data-ref="' + esc(q.reference) + '">⏸ Edit Hold Date</button>'
                  : (!isDeadLead ? '<button type="button" class="rqd-hold-lead-btn" data-ref="' + esc(q.reference) + '">⏸ Put On Hold</button>' : '');
                /* Ryan 2026-05-16: Reassign Rep sits directly
                   above Delete Quote (was: Reassign came BEFORE
                   Mark Dead, leaving two items between it and
                   Delete). Consistent positioning across all
                   order types — Reassign is always the second-
                   to-last admin action. */
                return primaryBtn +
                  secondaryActionBtn +
                  holdBtn +
                  (canMarkDead ? '<button type="button" class="rqd-mark-dead-btn" data-ref="' + esc(q.reference) + '">💀 Mark Dead</button>' : '') +
                  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08) — an ON HOLD deal is one of
                     the states the customer most often buys online out from under. This branch
                     had no link action at all, so the cog offered only "Link as Same Deal",
                     which cannot do the job. */
                  buildLinkExternalButton(q) +
                  (isAdmin ? '<button type="button" class="rqd-reassign-btn" data-ref="' + esc(q.reference) + '">Reassign Rep</button>' : '') +
                  (isAdmin ? '<button type="button" class="rqd-delete-quote-btn" data-ref="' + esc(q.reference) + '" data-draft-id="' + esc(q.id || '') + '" data-is-lead="1">Delete Quote</button>' : '');
              })()
          : isPending
            ? (function() {
                /* Ryan 2026-05-16: PENDING cog menu gains Put On Hold
                   / Edit Hold Date. handleHoldLead now handles pending
                   refs (reads q.followUp.dueAt instead of q.holdUntil
                   and passes isPending=true to the hold modal so it
                   renders the long preset shelf + +14d default). */
                var _pendHasFu = !!(q.followUp && q.followUp.dueAt);
                var _pendHoldBtn = _pendHasFu
                  ? '<button type="button" class="rqd-edit-hold-btn" data-ref="' + esc(q.reference) + '">⏸ Edit Hold Date</button>'
                  : '<button type="button" class="rqd-hold-lead-btn" data-ref="' + esc(q.reference) + '">⏸ Put On Hold</button>';
                return '<a href="/pages/rep-quote-workstation?ref=' + encodeURIComponent(q.reference) + '&pending=1&from=dashboard">Create Quote</a>' +
                  _pendHoldBtn +
                  (isAdmin ? '<button type="button" class="rqd-reassign-btn" data-ref="' + esc(q.reference) + '">Reassign Rep</button>' : '') +
                  '<button type="button" class="rqd-delete-pending-btn" data-ref="' + esc(q.reference) + '">Delete</button>';
              })()
            : (isSold
              // Online orders are real Shopify sales, not rep-driven quotes —
              // there's no quote PDF to download, no draft to edit, and no
              // rep tag to reassign. Strip Download PDF, Edit Quote, and
              // Reassign Rep for those rows. Generate Invoice / logistics
              // request / delete still apply.
              ? (!isAdmin ? '<a href="/pages/rep-quote-workstation?ref=' + encodeURIComponent(q.reference) + '&preview=1&from=dashboard">View Quote</a>' : '') +
                (q.source === 'online' ? '' : '<button type="button" class="rqd-download-pdf-btn" data-ref="' + esc(q.reference) + '">Download PDF</button>') +
                /* CUSTOM_RFQPORTAL_BY_CLAUDE — cancelled rows: only "View in Shopify" */
                (q.cancelled
                  ? '<a target="_blank" rel="noopener" href="https://admin.shopify.com/store/62c083-4/orders/' + esc((q.id || '').split('/').pop()) + '">View in Shopify Admin</a>'
                  :
                /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
                /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-22) - NEVER OFFER TO INVOICE A REPLACEMENT.
                   An RPL-* order is $0: the customer already paid for these goods. This button's
                   only gate was !q.linkedParentRef, which a replacement deliberately does NOT set
                   (that field feeds the balance rollup), so it rendered - and clicking it prompts
                   "Email it to the customer now?" straight into /send-invoice. The worker now
                   refuses both endpoints outright; this just stops offering it. */
                /* CUSTOM_REVIEWS_BY_CLAUDE (Ryan 2026-09-09) — the review ask (state, when Abby
                   asks, send / skip) moved here from the To Do cell's ⭐, which the "Closed ✓" pill
                   hid. Self-gates to the deal owner or an admin; '' when nothing to show.
                   Mirrored in dashboard-detail-shell.js (mobile cog). */
                buildReviewAskMenuItem(q) +
                (!q.linkedParentRef && !q.isReplacement ? '<button type="button" class="rqd-generate-invoice-btn" data-ref="' + esc(q.reference) + '">' + (q.invoiced ? 'Regenerate Invoice' : 'Generate Invoice') + '</button>' : '') +
                (isAdmin && q.source !== 'online' ? '<a href="/pages/rep-quote-workstation?ref=' + encodeURIComponent(q.reference) + '&from=dashboard">Edit Quote</a>' : '') +
                /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-10) — Duplicate a SOLD rep deal into
                   a fresh independent deal (replicate a proven config for another location). */
                (q.source !== 'online' ? '<button type="button" class="rqd-duplicate-quote-btn" data-ref="' + esc(q.reference) + '">⧉ Duplicate to New Deal</button>' : '') +
                /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-21) — CREATE REPLACEMENT ORDER.
                   Part of a delivery arrived damaged (or defective, or wrong): we owe the
                   customer a re-ship now and we chase the carrier for a claim separately.
                   Spawns a $0-to-customer RPL-* order that generates the supplier PO and
                   tracks the claim. Admin-only — it commits us to a PO and a company loss.
                   Hidden on a replacement (no replacements of replacements) and on a
                   balance child (an accounting construct, not a shipment). */
                /* Hidden on ONLINE orders too (Ryan 2026-08-22): findDraftOrderByRef searches
                   `tag:{ref}` among DRAFT orders and an online order has none, so the route
                   always answered 404 "Order not found". Matches the Duplicate button above. */
                (isAdmin && !q.cancelled && !q.isReplacement && !q._isBalanceChild && q.source !== 'online'
                  ? '<button type="button" class="rqd-create-replacement-btn" data-ref="' + esc(q.reference) + '">↩ Create Replacement Order</button>' : '') +
                (isAdmin && q.soldBasis && q.soldBasis.source === 'external' ? '<button type="button" class="rqd-unlink-external-btn" data-ref="' + esc(q.reference) + '">Unlink External Order</button>' : '') +
                /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08) — a deal already flipped SOLD
                   (rep marked it when the customer said they'd bought) still needs to be tied to
                   the actual order; self-gates off once it IS externally linked, leaving Unlink
                   above as the only link action. Also offers the order-side link on an online
                   row that matches an open deal — this is the branch online orders render. */
                buildLinkExternalButton(q) +
                buildLinkOpenDealButton(q) +
                (q.freightStatus === 'requested' && !q.linkedParentRef ? '<button type="button" class="rqd-cancel-freight-btn" data-ref="' + esc(q.reference) + '">Cancel Freight Request</button>' : '') + (q.installationStatus === 'requested' && !q.linkedParentRef ? '<button type="button" class="rqd-cancel-install-btn" data-ref="' + esc(q.reference) + '">Cancel Installation Request</button>' : '') /* CUSTOM_REQUESTCANCEL_BY_CLAUDE (Ryan 2026-10-13) */ +
                /* CUSTOM_RFQPORTAL_BY_CLAUDE — Shopify cancel + refund (admin, online, not already cancelled) */
                /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-07-29) — relabelled "Cancel and/or
                   Refund". The button used to promise a refund but only ever did the CANCEL
                   cascade (cancel + full refund + PO cancel + deal → CANCELLED, which also
                   reverses the rep's commission). Refunding PART of a deal that stays sold is
                   a different job, so this now opens a chooser: cancel the whole thing, or
                   refund some of it — the latter routes into the Adjust Order panel rather
                   than forking a second money path. */
                /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-05) — offline deals reach this too.
                   An OFFLINE deal can still be paid by card (rep sends a Shopify invoice, customer
                   pays — soldBasis.paidOnline), leaving a real order with a real SALE transaction
                   that Shopify can partially refund. RW-1260-A / SHOP-1862 is the case: it had a
                   refundable $3,040.40 Visa charge but the cog offered only all-or-nothing "Cancel
                   Order", because this branch was gated on source === 'online'. Now any admin-
                   visible SOLD, uncancelled deal with a resolvable paid order gets the chooser —
                   which routes a PARTIAL refund into the Adjust Order panel (one money path) and
                   leaves the full-cancel cascade to the Cancel Order button below.
                   Offline rows pass the PAID order's gid/name (q.paidOrderGid / q.paidOrderName),
                   not q.id — q.id is the draft order on those rows. */
                (isAdmin && (q.source === 'online' || (isSold && !q.cancelled && q.hasPaidOrder))
                  ? '<button type="button" class="rqd-cancel-online-btn" data-ref="' + esc(q.reference) + '" data-order-gid="' + esc(q.source === 'online' ? (q.id || '') : (q.paidOrderGid || '')) + '" data-order-name="' + esc(q.source === 'online' ? (q.orderName || q.reference) : (q.paidOrderName || '')) + '" data-amount="' + esc(q.total || '0') + '"' + (q.source !== 'online' ? ' data-offline="1"' : '') + '>Cancel and/or Refund</button>'
                  : '') +
                /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
                /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-08) — Cancel Order for a rep-created
                   sold deal: one click cancels the Shopify order + full refund, cancels the
                   PO(s), and flips the deal to CANCELLED. Gated to a sold deal with a paid
                   Shopify order; the worker blocks it if a vendor has already shipped. */
                (isAdmin && q.source !== 'online' && isSold && !q.cancelled && q.hasPaidOrder
                  ? '<button type="button" class="rqd-cancel-order-btn" data-ref="' + esc(q.reference) + '" data-order-name="' + esc(q.paidOrderName || '') + '" data-amount="' + esc(q.total || '0') + '">Cancel Order</button>'
                  : '') +
                /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
                /* CUSTOM_RFQPORTAL_BY_CLAUDE — Balance order link/unlink. Link
                   opens a modal that ties a secondary Shopify order (upsell,
                   substitution, etc.) to this parent so they roll up as one
                   sale on the dashboard and the PO generator can swap SKUs. */
                (isAdmin && (!q.balanceChildren || !q.balanceChildren.length)
                  ? '<button type="button" class="rqd-link-balance-btn" data-ref="' + esc(q.reference) + '" data-order-gid="' + esc(q.id || '') + '">Link Balance Order</button>'
                  : '') +
                (isAdmin && q.balanceChildren && q.balanceChildren.length
                  ? '<button type="button" class="rqd-link-balance-btn" data-ref="' + esc(q.reference) + '" data-order-gid="' + esc(q.id || '') + '">Link Another Balance Order</button>'
                  : '') +
                /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
                /* Ryan 2026-05-16: Reassign Rep directly above Delete Quote. */
                (isAdmin && q.source !== 'online' ? '<button type="button" class="rqd-reassign-btn" data-ref="' + esc(q.reference) + '">Reassign Rep</button>' : '') +
                (isAdmin && q.source !== 'online' ? '<button type="button" class="rqd-delete-quote-btn" data-ref="' + esc(q.reference) + '" data-draft-id="' + esc(q.id || '') + '"' + (isLead ? ' data-is-lead="1"' : '') + '>Delete Quote</button>' : '') +
                buildRequestLogisticsButtons(q)
                /* CUSTOM_RFQPORTAL_BY_CLAUDE — close cancelled-branch ternary */
                )
                /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
              : '<a href="/pages/rep-quote-workstation?ref=' + encodeURIComponent(q.reference) + '&from=dashboard">Edit Quote</a>' +
                '<button type="button" class="rqd-download-pdf-btn" data-ref="' + esc(q.reference) + '">Download PDF</button>' +
                (q.signedAwaitingPayment ? '<button type="button" class="rqd-reverse-signature-btn" data-ref="' + esc(q.reference) + '">Reverse signature</button>' : '') + /* CUSTOM_SIGNEDUNPAID_BY_CLAUDE (card 0a13de9f8439) */
                '<button type="button" class="rqd-copy-pay-link-btn" data-ref="' + esc(q.reference) + '">Copy payment link</button>' + /* CUSTOM_ONLINESIGN_BY_CLAUDE (Ryan 2026-10-26) */
                /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-10) — Duplicate to New Deal.
                   Forks this quote into a brand-new independent deal (own DD line) for the
                   multi-location case. Owner/admin only (server enforces); never on online orders. */
                (q.source !== 'online' ? '<button type="button" class="rqd-duplicate-quote-btn" data-ref="' + esc(q.reference) + '">⧉ Duplicate to New Deal</button>' : '') +
                /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-05) — keep hold controls
                   for a PROGRESSED on-hold deal routed here (was lead-menu only).
                   Same class hooks, already event-delegated. */
                (isOnHold ? '<button type="button" class="rqd-wake-lead-btn" data-ref="' + esc(q.reference) + '">⏵ Remove Hold</button>' +
                            '<button type="button" class="rqd-edit-hold-btn" data-ref="' + esc(q.reference) + '">⏸ Edit Hold Date</button>' : '') +
                buildRequestLogisticsButtons(q) +
                /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08) — was an inline admin ternary
                   here and ONLY here; now the shared self-gating builder, so the other branches
                   render the same action. buildLinkOpenDealButton rides along because an online
                   order with no lifecycle record resolves lsLabel to 'quoted', not 'sold', and
                   lands in THIS branch rather than the SOLD one — which is precisely the
                   never-touched-by-a-rep order the match flag exists to catch. */
                buildLinkExternalButton(q) +
                buildLinkOpenDealButton(q) +
                /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-03) — "Link to inquiry":
                   reattach an original lead (form + ad attribution) to a STANDALONE
                   quote built fresh instead of from the lead. Self-hides unless the
                   customer actually has an OPEN inquiry (q.hasOpenInquiry, stamped by
                   the worker) so it never gets confused with "Link as Same Deal". */
                (isAdmin && q.hasOpenInquiry ? '<button type="button" class="rqd-link-inquiry-btn" data-ref="' + esc(q.reference) + '" data-lead-ref="' + esc(q.openInquiryRef || '') + '">🔗 Link to inquiry</button>' : '') +
                (q.freightStatus === 'requested' && !q.linkedParentRef ? '<button type="button" class="rqd-cancel-freight-btn" data-ref="' + esc(q.reference) + '">Cancel Freight Request</button>' : '') + (q.installationStatus === 'requested' && !q.linkedParentRef ? '<button type="button" class="rqd-cancel-install-btn" data-ref="' + esc(q.reference) + '">Cancel Installation Request</button>' : '') /* CUSTOM_REQUESTCANCEL_BY_CLAUDE (Ryan 2026-10-13) */ +
                /* Ryan 2026-05-16: Reassign Rep directly above Delete Quote. */
                (isAdmin ? '<button type="button" class="rqd-reassign-btn" data-ref="' + esc(q.reference) + '">Reassign Rep</button>' : '') +
                (isAdmin ? '<button type="button" class="rqd-delete-quote-btn" data-ref="' + esc(q.reference) + '" data-draft-id="' + esc(q.id || '') + '"' + (isLead ? ' data-is-lead="1"' : '') + '>Delete Quote</button>' : '')
            )
          ) +
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-24) — Deal grouping ("Same
             Deal" link). Group two chains for the same customer into one deal:
             one cascade, one Money Moves bell, one unit in every count. Server
             enforces same-owner. Hidden on balance children + cancelled rows. */
          ((q.cancelled || q._isBalanceChild) ? '' :
            (q.isDealGroup
              ? '<button type="button" class="rqd-link-deal-btn" data-ref="' + esc(q.reference) + '">🔗 Link Another Deal</button>' +
                '<button type="button" class="rqd-ungroup-deal-btn" data-ref="' + esc(q.reference) + '">🔗 Ungroup This Deal</button>' +
                (q.linkGroupIsPrimaryBase ? '' : '<button type="button" class="rqd-set-primary-deal-btn" data-ref="' + esc(q.reference) + '">⭐ Make Group Primary</button>')
              : '<button type="button" class="rqd-link-deal-btn" data-ref="' + esc(q.reference) + '">🔗 Link as Same Deal</button>')) +
        '</div>' +
      '</div></td>' +
      '<td class="rqd-col-mobile-actions"><button class="rqd-actions-trigger rqd-actions-trigger--mobile" type="button" data-menu-ref="' + esc(q.reference) + '">&#9881; Options &#8250;</button></td>';
    return tr;
  }

  function initQuoteRow(deps) {
    deps = deps || {};
    esc = deps.esc;
    isResidentialOrg = deps.isResidentialOrg;
    formatDate = deps.formatDate;
    money = deps.money;
    capitalize = deps.capitalize;
    buildProgressBar = deps.buildProgressBar;
    _qstHasContactActivity = deps._qstHasContactActivity;
    buildRequestLogisticsButtons = deps.buildRequestLogisticsButtons;
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-08) — both self-gate and return '' when they
       don't apply, so every cog branch calls them unconditionally. No-op fallbacks keep an
       older main.js from throwing if the assets deploy out of order. */
    buildLinkExternalButton = deps.buildLinkExternalButton || function() { return ''; };
    buildLinkOpenDealButton = deps.buildLinkOpenDealButton || function() { return ''; };
    /* Shared with main.js's detail-panel tint — same predicate, so the LINKED pill and the
       purple panel can't drift. Fallback keeps an out-of-order asset deploy from throwing. */
    hasExternalOrderLink = deps.hasExternalOrderLink || function(q) {
      /* Keep in lockstep with main.js: OFFLINE rows only. An online order keeps its own
         Shopify pill and green theme — the linked statuses are offline-pipeline language. */
      if (!q || q.source === 'online') return false;
      return !!((q.soldBasis && q.soldBasis.source === 'external') || q.externalLinkedToRef);
    };
    getIsAdmin = deps.getIsAdmin || function() { return false; };
    getCurrentSource = deps.getCurrentSource || function() { return 'offline'; };
    buildReviewAskMenuItem = deps.buildReviewAskMenuItem || function() { return ''; };
  }

  window.RQD.buildQuoteRow = buildQuoteRow;
  window.RQD.buildAgentOwnerChip = buildAgentOwnerChip; /* CUSTOM_AGENTOWNER_BY_CLAUDE: drawn in the LINKED FILES column */
  window.RQD.initQuoteRow = initQuoteRow;
  /* CUSTOM_READYBY_BY_CLAUDE (AIOPS b641360cdb02, 2026-09-16) — ONE formatter for a supplier's
     ready-by, shared with the Tracking card. The board chip and the card must never disagree about
     what a supplier said; two copies of this would, the first time one of them learned a new
     precision word. */
  window.RQD.readyByShort = readyByShort;
  window.RQD.readyByRangeText = readyByRangeText;
})();
